// reportExport.js
// ─────────────────────────────────────────────────────────────────────────────
// Backend `/reports/generate` sekarang SELALU balikin JSON polos
// ({ title, dataset, total, rows }) — dia gak lagi ngerjain konversi ke
// csv/xml/xlsx/pdf. Semua konversi format export dipindah ke sini, di
// frontend, supaya backend tetap simpel dan bebas dipakai buat render tabel
// biasa juga (bukan cuma buat di-download).
import React from 'react';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';


function triggerDownload(content, filename, mimeType) {
  const blob = content instanceof Blob ? content : new Blob([content], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function csvEscape(v) {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function rowsToCSV(rows) {
  if (!rows.length) return '';
  const headers = Object.keys(rows[0]);
  const lines = [headers.map(csvEscape).join(',')];
  rows.forEach(r => lines.push(headers.map(h => csvEscape(r[h])).join(',')));
  return lines.join('\n');
}

function xmlEscape(v) {
  return String(v ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&apos;');
}

function xmlTag(key) {
  let t = String(key).trim().replace(/[^a-zA-Z0-9_-]/g, '_');
  if (!t || /^[0-9]/.test(t)) t = `col_${t}`;
  return t;
}

export function rowsToXML(rows, title = 'Report') {
  const body = rows.map(row => {
    const fields = Object.entries(row).map(([k, v]) => `    <${xmlTag(k)}>${xmlEscape(v)}</${xmlTag(k)}>`).join('\n');
    return `  <row>\n${fields}\n  </row>`;
  }).join('\n');
  return `<?xml version="1.0" encoding="UTF-8"?>\n<report title="${xmlEscape(title)}" generated_at="${new Date().toISOString()}" total_rows="${rows.length}">\n${body}\n</report>\n`;
}

// XLSX butuh library SheetJS (`xlsx`). Di-load lazy (dynamic import) supaya
// gak nge-bloat bundle awal buat user yang gak pernah export ke Excel.
async function rowsToXLSXBlob(rows, title = 'Report') {
  const XLSX = await import('xlsx');
  const headers = rows.length ? Object.keys(rows[0]) : [];
  const data = [headers, ...rows.map(r => headers.map(h => r[h]))];
  const ws = XLSX.utils.aoa_to_sheet(data);
  ws['!cols'] = headers.map(() => ({ wch: 18 }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, String(title).replace(/[[\]:*?/\\]/g, '').slice(0, 31) || 'Report');
  const out = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
  return new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

// PDF "tabel" — dirender lewat print dialog browser (Save as PDF), tanpa
// nambah dependency berat kayak jsPDF. Cukup buat kebutuhan tabel data biasa.
function printRowsAsPDF(rows, title = 'Report') {
  const headers = rows.length ? Object.keys(rows[0]) : [];
  const win = window.open('', '_blank');
  if (!win) throw new Error('Popup diblokir browser — izinkan popup untuk export PDF.');

  const style = `
    body { font-family: -apple-system, Arial, sans-serif; padding: 24px; color: #111; }
    h1 { font-size: 16px; margin: 0 0 2px; }
    .meta { font-size: 11px; color: #666; margin-bottom: 14px; }
    table { width: 100%; border-collapse: collapse; font-size: 10px; }
    th, td { padding: 5px 8px; text-align: left; border-bottom: 1px solid #ddd; white-space: nowrap; }
    th { background: #ddd; font-weight: 700; }
    @media print { @page { size: A4 landscape; margin: 14mm; } }
  `;
  const theadHtml = `<tr>${headers.map(h => `<th>${xmlEscape(h)}</th>`).join('')}</tr>`;
  const tbodyHtml = rows.map(r => `<tr>${headers.map(h => `<td>${xmlEscape(r[h])}</td>`).join('')}</tr>`).join('');

  win.document.write(`<!DOCTYPE html><html><head><title>${xmlEscape(title)}</title><style>${style}</style></head><body>
    <h1>${xmlEscape(title)}</h1>
    <div class="meta">Generated: ${new Date().toLocaleString()} · ${rows.length} baris</div>
    ${rows.length ? `<table><thead>${theadHtml}</thead><tbody>${tbodyHtml}</tbody></table>` : '<p>Tidak ada data untuk filter/periode ini.</p>'}
  </body></html>`);
  win.document.close();
  win.focus();
  // Kasih waktu browser render sebelum munculin print dialog
  setTimeout(() => win.print(), 300);
}

// ── PDF: chart (histogram & concurrent) ──────────────────────────────────────
// Dataset timeseries (histogram/concurrent) lebih enak dibaca sebagai grafik
// garis daripada tabel angka mentah. Chart-nya sekarang bener-bener dirender
// pakai Recharts (komponen <LineChart> yang sama kayak di Analytics.jsx),
// bukan digambar manual sebagai SVG — jadi bentuk garis, kurva "monotone",
// dan tick-nya identik. Caranya: render <LineChart> ke <div> tersembunyi
// (di luar viewport), ambil markup <svg> hasil render Recharts, lalu tempel
// ke window print yang sama kayak PDF tabel — user tinggal "Save as PDF".
// Tabel statistik (Last/Max/Avg/Min) di bawah chart tetap dibikin manual,
// persis kayak "legend" di Analytics.jsx.

// Sama persis dengan COLORS di src/pages/Analytics.jsx (accent-blue, cyan,
// green, orange, purple) — nilai hex diambil dari src/index.css (tema
// terang), dipakai literal (bukan var(--...)) supaya PDF selalu konsisten
// terang walau dashboard user lagi pakai dark mode.
const CHART_PALETTE = ['#3b82f6', '#06b6d4', '#10b981', '#f59e0b', '#8b5cf6'];

const THEME = {
  border: '#e2e8f0',
  textPrimary: '#0f172a',
  textSecondary: '#475569',
  textMuted: '#94a3b8',
  red: '#ef4444',
  cyan: '#06b6d4',
  green: '#10b981',
};

// Tiap dataset timeseries direpresentasikan sebagai SEPASANG metrik yang
// digambar dengan warna sama per grup, dibedakan lewat garis solid (metrik
// pertama) vs dashed (metrik kedua) — persis pola "↑/↓" dan
// "(users)/(sessions)" yang dipakai di Analytics.jsx. `keys` didaftarkan
// dobel (camelCase + snake_case beberapa alias umum) supaya tetap kedeteksi
// walau kolom di-alias sedikit beda lewat "Field Mapping" di form Report.
const DATASET_METRIC_PAIRS = {
  histogram: {
    aKeys: ['upload_bytes', 'upload', 'bytes_up'],
    bKeys: ['download_bytes', 'download', 'bytes_down'],
    aLabel: 'Upload', bLabel: 'Download',
    aSuffix: '↑', bSuffix: '↓',
    formatValue: humanBytes,
    formatAvg: humanBytes,
  },
  concurrent: {
    aKeys: ['active_users', 'users'],
    bKeys: ['active_sessions', 'sessions'],
    aLabel: 'Active Users', bLabel: 'Active Sessions',
    aSuffix: '(users)', bSuffix: '(sessions)',
    formatValue: (v) => String(Math.round(v)),
    formatAvg: (v) => v.toFixed(1),
  },
};

// Grup dimensi dibatasi biar chart tetap kebaca (kalau group_by di report
// punya banyak nilai unik, misal ratusan MAC address, chart bakal jadi
// spaghetti gak berguna) — ambil TOP N berdasarkan total value tertinggi.
const CHART_MAX_GROUPS = 5;

// ── Facet chart ──────────────────────────────────────────────────────────
// Kalau report di-group by lebih dari satu dimensi (mis. realm + username,
// atau realm + NAS), satu chart gabungan gampang jadi spaghetti karena tiap
// nilai facet bisa punya banyak sub-grup sendiri. Jadi chart-nya dipecah:
// tiap nilai facet dapet grafik + tabel statistik sendiri-sendiri, ditumpuk
// per halaman di PDF yang sama. Kolom & label facet-nya normalnya dikirim
// dari Reports.jsx (dihitung dari group_by + field mapping yang beneran
// dipakai) — daftar di bawah cuma fallback tebakan kalau hint itu gak ada
// (mis. dipanggil dari alur lain).
const FACET_KEY_CANDIDATES = ['nasipaddress', 'nas_ip', 'nasip', 'nas'];
const CHART_MAX_FACETS = 8; // batasi biar PDF gak kepanjangan kalau nilai facet-nya puluhan

function humanBytes(n) {
  const v0 = Number(n) || 0;
  if (v0 === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = v0, i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(i > 0 && v < 10 ? 1 : 0)} ${units[i]}`;
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

// Cari nama kolom pertama dari daftar kandidat yang beneran ada di rows.
function resolveKey(rows, candidates) {
  return candidates.find(k => rows.some(r => r[k] !== undefined)) || null;
}

// Format bucket persis seperti fmtBucket() di Analytics.jsx: "6 Jul, 08.00"
function formatBucketLabel(b) {
  if (!b) return '';
  const d = new Date(b);
  if (isNaN(d)) return String(b);
  return d.toLocaleString('id-ID', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
}

// Susun ulang rows timeseries mentah jadi { data, series } — persis
// transformasi bucketMap di load()/loadConcurrent() Analytics.jsx: satu baris
// per bucket, satu dataKey per (grup × metrik).
// `excludeKeys` dipakai waktu mode facet: kolom yang sudah jadi facet (mis.
// NAS) gak perlu diulang lagi di label tiap garis di dalam chart facet-nya.
function buildChartModel(rows, dataset, { excludeKeys = [] } = {}) {
  const cfg = DATASET_METRIC_PAIRS[dataset];
  if (!cfg || !rows.length) return null;

  const aKey = resolveKey(rows, cfg.aKeys);
  const bKey = resolveKey(rows, cfg.bKeys);
  if (!aKey && !bKey) return null;

  const dimensionKeys = Object.keys(rows[0]).filter(k => k !== 'bucket' && k !== aKey && k !== bKey && !excludeKeys.includes(k));
  const groupOf = (row) => dimensionKeys.length ? dimensionKeys.map(k => row[k] ?? '—').join(' / ') : null;

  // Urutan bucket dipertahankan sesuai urutan kedatangan (backend sudah ORDER BY bucket ASC)
  const buckets = [];
  const bucketIndex = new Map();
  rows.forEach(r => {
    const b = String(r.bucket);
    if (!bucketIndex.has(b)) { bucketIndex.set(b, buckets.length); buckets.push(b); }
  });

  // Kumpulkan total per grup dulu, buat nentuin TOP N grup mana yang dipakai
  const groupTotals = new Map();
  rows.forEach(r => {
    const g = groupOf(r) ?? 'Total';
    const t = num(r[aKey]) + num(r[bKey]);
    groupTotals.set(g, (groupTotals.get(g) || 0) + t);
  });
  const allGroups = [...groupTotals.keys()];
  const topGroups = allGroups.sort((a, b) => groupTotals.get(b) - groupTotals.get(a)).slice(0, CHART_MAX_GROUPS);
  const truncatedGroups = allGroups.length > topGroups.length;

  const aValues = new Map(), bValues = new Map();
  topGroups.forEach(g => {
    aValues.set(g, new Array(buckets.length).fill(0));
    bValues.set(g, new Array(buckets.length).fill(0));
  });
  rows.forEach(r => {
    const g = groupOf(r) ?? 'Total';
    if (!topGroups.includes(g)) return;
    const idx = bucketIndex.get(String(r.bucket));
    aValues.get(g)[idx] += num(r[aKey]);
    bValues.get(g)[idx] += num(r[bKey]);
  });

  // Tiap grup -> 2 series (solid + dashed) dengan warna yang sama, mengikuti
  // konvensi label Analytics.jsx: "{grup} ↑/↓" atau "{grup} (users/sessions)"
  // kalau di-group, atau cuma "Upload/Download" & "Active Users/Sessions"
  // kalau totalan (gak ada group by).
  const series = topGroups.flatMap((g, i) => {
    const color = CHART_PALETTE[i % CHART_PALETTE.length];
    const grouped = g !== 'Total' || dimensionKeys.length;
    return [
      { dataKey: `s${i}_a`, label: grouped ? `${g} ${cfg.aSuffix}` : cfg.aLabel, color, dash: false, values: aValues.get(g) },
      { dataKey: `s${i}_b`, label: grouped ? `${g} ${cfg.bSuffix}` : cfg.bLabel, color, dash: true,  values: bValues.get(g) },
    ];
  });

  // Data per-bucket buat dikonsumsi <LineChart data={...}> Recharts
  const data = buckets.map((b, idx) => {
    const row = { bucket: b };
    series.forEach(s => { row[s.dataKey] = s.values[idx]; });
    return row;
  });

  return { data, series, cfg, truncatedGroups, groupCount: allGroups.length };
}

// Render <LineChart> Recharts ke <div> tersembunyi lalu balikin markup
// <svg>-nya. Dipakai supaya chart di PDF export identik dengan chart di
// Analytics (line library, kurva, tick — sama semua), bukan pendekatan.
async function renderRechartsSVG({ data, series, width, height, yFormatter }) {
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-99999px';
  container.style.top = '0px';
  container.style.width = `${width}px`;
  container.style.height = `${height}px`;
  document.body.appendChild(container);

  const root = createRoot(container);

  try {
    const chartEl = React.createElement(
      LineChart,
      { width, height, data, margin: { top: 8, right: 20, bottom: 5, left: 10 } },
      React.createElement(CartesianGrid, { strokeDasharray: '3 3', stroke: THEME.border }),
      React.createElement(XAxis, { dataKey: 'bucket', tickFormatter: formatBucketLabel, tick: { fontSize: 11, fill: THEME.textMuted } }),
      React.createElement(YAxis, { tickFormatter: yFormatter, tick: { fontSize: 11, fill: THEME.textMuted }, width: 80 }),
      ...series.map(s => React.createElement(Line, {
        key: s.dataKey, type: 'monotone', dataKey: s.dataKey,
        stroke: s.color, strokeWidth: 2, dot: false,
        strokeDasharray: s.dash ? '4 2' : undefined,
        isAnimationActive: false,
      })),
    );

    flushSync(() => { root.render(chartEl); });
    // Beberapa perhitungan layout Recharts (posisi tick, dsb) kelar di efek
    // setelah commit — kasih waktu 2 frame biar SVG-nya settle sebelum dibaca.
    await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    const svgEl = container.querySelector('svg');
    return svgEl ? svgEl.outerHTML : null;
  } finally {
    root.unmount();
    container.remove();
  }
}

// Tabel statistik Series/Last/Max/Avg/Min — persis gaya "legend" di
// Analytics.jsx: swatch garis solid/dashed + warna per series, kolom
// Max/Avg/Min pakai warna tetap (merah/cyan/hijau) terlepas dari warna series-nya.
function buildStatsTable(series, cfg) {
  const statsRows = series.map(s => {
    const vals = s.values;
    const max = vals.length ? Math.max(...vals) : 0;
    const min = vals.length ? Math.min(...vals) : 0;
    const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
    const last = vals.length ? vals[vals.length - 1] : 0;
    const lineIcon = `<svg width="20" height="10" style="vertical-align:middle;"><line x1="0" y1="5" x2="20" y2="5" stroke="${s.color}" stroke-width="2" stroke-dasharray="${s.dash ? '4 2' : 'none'}" /></svg>`;
    return `<tr>
      <td style="padding:5px 10px;white-space:nowrap;">${lineIcon} <span style="color:${s.color};font-weight:500;">${xmlEscape(s.label)}</span></td>
      <td style="padding:5px 10px;text-align:right;font-family:'Courier New',monospace;color:${THEME.textPrimary};font-weight:600;">${xmlEscape(cfg.formatValue(last))}</td>
      <td style="padding:5px 10px;text-align:right;font-family:'Courier New',monospace;color:${THEME.red};">${xmlEscape(cfg.formatValue(max))}</td>
      <td style="padding:5px 10px;text-align:right;font-family:'Courier New',monospace;color:${THEME.cyan};">${xmlEscape(cfg.formatAvg(avg))}</td>
      <td style="padding:5px 10px;text-align:right;font-family:'Courier New',monospace;color:${THEME.green};">${xmlEscape(cfg.formatValue(min))}</td>
    </tr>`;
  }).join('');

  const statsHead = ['Series', 'Last', 'Max', 'Avg', 'Min']
    .map((h, i) => `<th style="padding:4px 10px;text-align:${i === 0 ? 'left' : 'right'};color:${THEME.textMuted};font-weight:600;font-size:10px;text-transform:uppercase;">${h}</th>`)
    .join('');

  return `<table style="width:100%;border-collapse:collapse;font-size:12px;">
    <thead><tr>${statsHead}</tr></thead>
    <tbody>${statsRows}</tbody>
  </table>`;
}

async function printChartAsPDF(rows, dataset, title = 'Report', facetHintKey = null, facetHintLabel = 'Grup') {
  const cfg = DATASET_METRIC_PAIRS[dataset];
  if (!cfg || !rows.length) return printRowsAsPDF(rows, title); // fallback: dataset gak dikenal / gak ada baris

  const aKey = resolveKey(rows, cfg.aKeys);
  const bKey = resolveKey(rows, cfg.bKeys);
  if (!aKey && !bKey) return printRowsAsPDF(rows, title); // gak ada metrik numerik sama sekali

  // Kalau hasil query punya kolom facet (dimensi group_by paling spesifik,
  // mis. username atau NAS) dan isinya lebih dari satu nilai unik, pecah
  // jadi chart per-nilai (facet) daripada satu chart gabungan yang bisa
  // spaghetti. `facetHintKey`/`facetHintLabel` (dikirim dari Reports.jsx,
  // dihitung dari group_by + field mapping yang beneran dipakai)
  // diprioritaskan karena pasti benar walau kolomnya di-alias; kalau gak
  // ada baru nebak dari nama kolom umum lewat FACET_KEY_CANDIDATES (mis.
  // buat report lama/tanpa hint).
  const facetKey = (facetHintKey && rows.some(r => r[facetHintKey] !== undefined))
    ? facetHintKey
    : resolveKey(rows, FACET_KEY_CANDIDATES);
  const facetLabel = (facetKey === facetHintKey) ? facetHintLabel : 'NAS';
  const hasMultipleFacets = facetKey && new Set(rows.map(r => r[facetKey] ?? '—')).size > 1;
  if (hasMultipleFacets) {
    return printFacetedChartAsPDF(rows, dataset, title, facetKey, facetLabel, aKey, bKey);
  }

  const model = buildChartModel(rows, dataset);
  if (!model) return printRowsAsPDF(rows, title);

  const width = Math.min(1400, Math.max(760, model.data.length * 46));
  const height = 360;
  const svgMarkup = await renderRechartsSVG({
    data: model.data, series: model.series, width, height, yFormatter: model.cfg.formatValue,
  });

  // Kalau render Recharts gagal (misal browser gak support createRoot di
  // konteks ini), tetap jatuh ke tabel biasa daripada nge-blank.
  if (!svgMarkup) return printRowsAsPDF(rows, title);

  const statsTable = buildStatsTable(model.series, model.cfg);

  const win = window.open('', '_blank');
  if (!win) throw new Error('Popup diblokir browser — izinkan popup untuk export PDF.');

  const style = `
    body { font-family: -apple-system, Arial, sans-serif; padding: 24px; color: ${THEME.textPrimary}; }
    h1 { font-size: 16px; margin: 0 0 2px; }
    .meta { font-size: 11px; color: ${THEME.textSecondary}; margin-bottom: 14px; }
    .chart-wrap svg { display: block; }
    .legend-wrap { margin-top: 14px; border-top: 1px solid ${THEME.border}; padding-top: 10px; }
    @media print { @page { size: A4 landscape; margin: 14mm; } }
  `;
  win.document.write(`<!DOCTYPE html><html><head><title>${xmlEscape(title)}</title><style>${style}</style></head><body>
    <h1>${xmlEscape(title)}</h1>
    <div class="meta">Generated: ${new Date().toLocaleString()} · ${rows.length} baris data${model.truncatedGroups ? ` · menampilkan ${CHART_MAX_GROUPS} grup teratas dari ${model.groupCount} grup (berdasarkan total tertinggi)` : ''}</div>
    <div class="chart-wrap">${svgMarkup}</div>
    <div class="legend-wrap">${statsTable}</div>
  </body></html>`);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 300);
}

// PDF chart mode "facet": satu grafik + tabel statistik per nilai facet,
// ditumpuk per halaman dalam satu PDF yang sama. Nilai facet dipilih
// berdasarkan total tertinggi terlebih dulu (sama seperti TOP N grup di
// buildChartModel), dibatasi CHART_MAX_FACETS biar PDF-nya gak kepanjangan.
async function printFacetedChartAsPDF(rows, dataset, title, facetKey, facetLabel, aKey, bKey) {
  const facetTotals = new Map();
  rows.forEach(r => {
    const fv = r[facetKey] ?? '—';
    facetTotals.set(fv, (facetTotals.get(fv) || 0) + num(r[aKey]) + num(r[bKey]));
  });
  const allFacets = [...facetTotals.keys()].sort((a, b) => facetTotals.get(b) - facetTotals.get(a));
  const limitedFacets = allFacets.slice(0, CHART_MAX_FACETS);
  const truncatedFacets = allFacets.length > limitedFacets.length;

  const sections = [];
  for (const fv of limitedFacets) {
    const facetRows = rows.filter(r => (r[facetKey] ?? '—') === fv);
    const model = buildChartModel(facetRows, dataset, { excludeKeys: [facetKey] });
    if (!model) continue;

    const width = Math.min(1400, Math.max(760, model.data.length * 46));
    const height = 300;
    const svgMarkup = await renderRechartsSVG({
      data: model.data, series: model.series, width, height, yFormatter: model.cfg.formatValue,
    });
    if (!svgMarkup) continue;

    sections.push({
      facetLabel: fv,
      svgMarkup,
      statsTable: buildStatsTable(model.series, model.cfg),
      truncatedGroups: model.truncatedGroups,
      groupCount: model.groupCount,
    });
  }

  // Kalau semua nilai facet gagal di-render (harusnya jarang), jatuh ke
  // tabel biasa daripada nge-blank.
  if (!sections.length) return printRowsAsPDF(rows, title);

  const win = window.open('', '_blank');
  if (!win) throw new Error('Popup diblokir browser — izinkan popup untuk export PDF.');

  const style = `
    body { font-family: -apple-system, Arial, sans-serif; padding: 24px; color: ${THEME.textPrimary}; }
    h1 { font-size: 16px; margin: 0 0 2px; }
    .meta { font-size: 11px; color: ${THEME.textSecondary}; margin-bottom: 18px; }
    .facet-section { margin-bottom: 30px; page-break-inside: avoid; }
    .facet-section.break { page-break-after: always; break-after: page; }
    .facet-title { font-size: 13px; font-weight: 700; margin: 0 0 8px; color: ${THEME.textPrimary}; border-left: 3px solid ${CHART_PALETTE[0]}; padding-left: 8px; }
    .facet-title .sub { font-weight: 400; color: ${THEME.textMuted}; font-size: 11px; }
    .chart-wrap svg { display: block; }
    .legend-wrap { margin-top: 10px; border-top: 1px solid ${THEME.border}; padding-top: 8px; }
    @media print { @page { size: A4 landscape; margin: 14mm; } }
  `;

  // Tiap nilai facet di halaman print-nya sendiri (kecuali yang terakhir,
  // biar gak nyisain 1 halaman kosong di akhir PDF) — sesuai permintaan
  // "page 1 3WWL, page 2 UDQN, dst".
  const bodyHtml = sections.map((s, i) => `
    <div class="facet-section${i < sections.length - 1 ? ' break' : ''}">
      <div class="facet-title">${xmlEscape(facetLabel)}: ${xmlEscape(s.facetLabel)}${s.truncatedGroups ? ` <span class="sub">(top ${CHART_MAX_GROUPS} dari ${s.groupCount} grup)</span>` : ''}</div>
      <div class="chart-wrap">${s.svgMarkup}</div>
      <div class="legend-wrap">${s.statsTable}</div>
    </div>
  `).join('');

  win.document.write(`<!DOCTYPE html><html><head><title>${xmlEscape(title)}</title><style>${style}</style></head><body>
    <h1>${xmlEscape(title)}</h1>
    <div class="meta">Generated: ${new Date().toLocaleString()} · ${rows.length} baris data · ${limitedFacets.length} ${xmlEscape(facetLabel)} ditampilkan${truncatedFacets ? ` dari ${allFacets.length} total (berdasarkan total tertinggi)` : ''}</div>
    ${bodyHtml}
  </body></html>`);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 300);
}


// ── PDF gabungan: Traffic (histogram) di atas + Concurrent Users di bawah ──
// Dipakai kalau user mau lihat traffic & jumlah user aktif dalam satu PDF,
// pakai group_by/filter/periode yang SAMA buat dua-duanya (Reports.jsx yang
// nge-query dataset histogram & concurrent secara terpisah lalu ngirim
// rows-nya ke sini). Non-faceted -> 1 halaman, 2 chart ditumpuk. Faceted
// (group_by lebih dari 1 dimensi, mis. realm+NAS) -> per nilai facet dapet
// halaman sendiri, tiap halaman isinya 2 chart ditumpuk juga.
async function renderMetricSection(rows, dataset, label, opts = {}) {
  const model = rows.length ? buildChartModel(rows, dataset, opts) : null;
  if (!model) return { label, empty: true };

  const width = Math.min(1400, Math.max(760, model.data.length * 46));
  const height = opts.height || 300;
  const svgMarkup = await renderRechartsSVG({
    data: model.data, series: model.series, width, height, yFormatter: model.cfg.formatValue,
  });
  if (!svgMarkup) return { label, empty: true };

  return {
    label, svgMarkup, statsTable: buildStatsTable(model.series, model.cfg),
    truncatedGroups: model.truncatedGroups, groupCount: model.groupCount,
  };
}

function metricSectionHTML(s, subtitleSize = 11) {
  if (s.empty) {
    return `<div class="chart-section"><div class="section-title">${xmlEscape(s.label)}</div><p style="color:${THEME.textMuted};font-size:${subtitleSize}px;">Tidak ada data.</p></div>`;
  }
  return `<div class="chart-section">
    <div class="section-title">${xmlEscape(s.label)}${s.truncatedGroups ? ` <span class="sub">(top ${CHART_MAX_GROUPS} dari ${s.groupCount} grup)</span>` : ''}</div>
    <div class="chart-wrap">${s.svgMarkup}</div>
    <div class="legend-wrap">${s.statsTable}</div>
  </div>`;
}

async function printCombinedTrafficConcurrentPDF(trafficRows, concurrentRows, title = 'Report', facetHintKey = null, facetHintLabel = 'Grup') {
  if (!trafficRows.length && !concurrentRows.length) {
    throw new Error('Tidak ada data traffic maupun concurrent untuk filter/periode ini.');
  }

  const facetKeyOf = (rows) => {
    if (!rows.length) return null;
    return (facetHintKey && rows.some(r => r[facetHintKey] !== undefined))
      ? facetHintKey
      : resolveKey(rows, FACET_KEY_CANDIDATES);
  };
  const facetKey = facetKeyOf(trafficRows) || facetKeyOf(concurrentRows);
  const facetLabel = facetKey === facetHintKey ? facetHintLabel : 'NAS';
  const hasMultipleFacets = facetKey
    && new Set([...trafficRows, ...concurrentRows].map(r => r[facetKey] ?? '—')).size > 1;

  if (hasMultipleFacets) {
    return printFacetedCombinedPDF(trafficRows, concurrentRows, title, facetKey, facetLabel);
  }

  const [trafficSection, concurrentSection] = await Promise.all([
    renderMetricSection(trafficRows, 'histogram', 'Traffic'),
    renderMetricSection(concurrentRows, 'concurrent', 'Concurrent Users'),
  ]);

  if (trafficSection.empty && concurrentSection.empty) {
    return printRowsAsPDF([...trafficRows, ...concurrentRows], title);
  }

  const win = window.open('', '_blank');
  if (!win) throw new Error('Popup diblokir browser — izinkan popup untuk export PDF.');

  const style = `
    body { font-family: -apple-system, Arial, sans-serif; padding: 24px; color: ${THEME.textPrimary}; }
    h1 { font-size: 16px; margin: 0 0 2px; }
    .meta { font-size: 11px; color: ${THEME.textSecondary}; margin-bottom: 18px; }
    .chart-section { margin-bottom: 26px; page-break-inside: avoid; }
    .section-title { font-size: 13px; font-weight: 700; margin: 0 0 8px; color: ${THEME.textPrimary}; border-left: 3px solid ${CHART_PALETTE[0]}; padding-left: 8px; }
    .section-title .sub { font-weight: 400; color: ${THEME.textMuted}; font-size: 11px; }
    .chart-wrap svg { display: block; }
    .legend-wrap { margin-top: 10px; border-top: 1px solid ${THEME.border}; padding-top: 8px; }
    @media print { @page { size: A4 landscape; margin: 14mm; } }
  `;
  win.document.write(`<!DOCTYPE html><html><head><title>${xmlEscape(title)}</title><style>${style}</style></head><body>
    <h1>${xmlEscape(title)}</h1>
    <div class="meta">Generated: ${new Date().toLocaleString()} · Traffic ${trafficRows.length} baris · Concurrent ${concurrentRows.length} baris</div>
    ${metricSectionHTML(trafficSection)}
    ${metricSectionHTML(concurrentSection)}
  </body></html>`);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 300);
}

async function printFacetedCombinedPDF(trafficRows, concurrentRows, title, facetKey, facetLabel) {
  const facetTotals = new Map();
  const addTotals = (rows, cfg) => {
    const aKey = resolveKey(rows, cfg.aKeys), bKey = resolveKey(rows, cfg.bKeys);
    rows.forEach(r => {
      const fv = r[facetKey] ?? '—';
      facetTotals.set(fv, (facetTotals.get(fv) || 0) + num(r[aKey]) + num(r[bKey]));
    });
  };
  addTotals(trafficRows, DATASET_METRIC_PAIRS.histogram);
  addTotals(concurrentRows, DATASET_METRIC_PAIRS.concurrent);

  const allFacets = [...facetTotals.keys()].sort((a, b) => facetTotals.get(b) - facetTotals.get(a));
  const limitedFacets = allFacets.slice(0, CHART_MAX_FACETS);
  const truncatedFacets = allFacets.length > limitedFacets.length;

  const pages = [];
  for (const fv of limitedFacets) {
    const tRows = trafficRows.filter(r => (r[facetKey] ?? '—') === fv);
    const cRows = concurrentRows.filter(r => (r[facetKey] ?? '—') === fv);
    const [trafficSection, concurrentSection] = await Promise.all([
      renderMetricSection(tRows, 'histogram', 'Traffic', { excludeKeys: [facetKey], height: 260 }),
      renderMetricSection(cRows, 'concurrent', 'Concurrent Users', { excludeKeys: [facetKey], height: 260 }),
    ]);
    if (trafficSection.empty && concurrentSection.empty) continue;
    pages.push({ facetValue: fv, trafficSection, concurrentSection });
  }

  if (!pages.length) return printRowsAsPDF([...trafficRows, ...concurrentRows], title);

  const win = window.open('', '_blank');
  if (!win) throw new Error('Popup diblokir browser — izinkan popup untuk export PDF.');

  const style = `
    body { font-family: -apple-system, Arial, sans-serif; padding: 24px; color: ${THEME.textPrimary}; }
    h1 { font-size: 16px; margin: 0 0 2px; }
    .meta { font-size: 11px; color: ${THEME.textSecondary}; margin-bottom: 18px; }
    .facet-page { margin-bottom: 30px; }
    .facet-page.break { page-break-after: always; break-after: page; }
    .facet-title { font-size: 14px; font-weight: 700; margin: 0 0 14px; color: ${THEME.textPrimary}; border-left: 3px solid ${CHART_PALETTE[0]}; padding-left: 8px; }
    .facet-title .sub { font-weight: 400; color: ${THEME.textMuted}; font-size: 11px; }
    .chart-section { margin-bottom: 20px; page-break-inside: avoid; }
    .section-title { font-size: 12px; font-weight: 700; margin: 0 0 6px; color: ${THEME.textSecondary}; }
    .chart-wrap svg { display: block; }
    .legend-wrap { margin-top: 8px; border-top: 1px solid ${THEME.border}; padding-top: 6px; }
    @media print { @page { size: A4 landscape; margin: 14mm; } }
  `;

  const bodyHtml = pages.map((p, i) => `
    <div class="facet-page${i < pages.length - 1 ? ' break' : ''}">
      <div class="facet-title">${xmlEscape(facetLabel)}: ${xmlEscape(p.facetValue)}</div>
      ${metricSectionHTML(p.trafficSection, 12)}
      ${metricSectionHTML(p.concurrentSection, 12)}
    </div>
  `).join('');

  win.document.write(`<!DOCTYPE html><html><head><title>${xmlEscape(title)}</title><style>${style}</style></head><body>
    <h1>${xmlEscape(title)}</h1>
    <div class="meta">Generated: ${new Date().toLocaleString()} · ${limitedFacets.length} ${xmlEscape(facetLabel)} ditampilkan${truncatedFacets ? ` dari ${allFacets.length} total (berdasarkan total tertinggi)` : ''}</div>
    ${bodyHtml}
  </body></html>`);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 300);
}

// Entry point buat export gabungan — dipanggil dari Reports.jsx setelah dia
// nge-query dataset histogram & concurrent secara terpisah (query yang sama
// gak bisa balikin dua dataset sekaligus dari satu call /reports/generate).
export async function exportCombinedTrafficConcurrentPDF(trafficRows, concurrentRows, title = 'Report', facetHintKey = null, facetHintLabel = 'Grup') {
  await printCombinedTrafficConcurrentPDF(trafficRows, concurrentRows, title, facetHintKey, facetHintLabel);
}

const slugify = (s) => String(s || 'report').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'report';

// Entry point utama: rows (Array<Object>) hasil dari reportsAPI.generate(),
// format ('csv'|'json'|'xml'|'xlsx'|'pdf'), title buat nama file/header,
// dataset ('histogram'|'concurrent'|'history') dipakai buat mutusin PDF-nya
// bentuk chart (timeseries) atau tabel (raw/history).
export async function exportRows(rows, format, title = 'Report', dataset = null, facetHintKey = null, facetHintLabel = 'Grup') {
  const filenameBase = slugify(title);

  switch (format) {
    case 'csv':
      triggerDownload(rowsToCSV(rows), `${filenameBase}.csv`, 'text/csv;charset=utf-8');
      break;
    case 'json':
      triggerDownload(JSON.stringify(rows, null, 2), `${filenameBase}.json`, 'application/json');
      break;
    case 'xml':
      triggerDownload(rowsToXML(rows, title), `${filenameBase}.xml`, 'application/xml');
      break;
    case 'xlsx': {
      const blob = await rowsToXLSXBlob(rows, title);
      triggerDownload(blob, `${filenameBase}.xlsx`, blob.type);
      break;
    }
    case 'pdf':
      if (DATASET_METRIC_PAIRS[dataset]) await printChartAsPDF(rows, dataset, title, facetHintKey, facetHintLabel);
      else printRowsAsPDF(rows, title);
      break;
    default:
      throw new Error(`Format tidak didukung: ${format}`);
  }
}